import {
  FinanceDistributionModelingGetDocument,
  FinanceDistributionModelingInvestorsListDocument,
  FinanceDistributionModelingPerformanceDocument,
  FinanceDistributionModelingPresetCreateDocument,
  FinanceDistributionModelingPresetDeleteDocument,
  FinanceDistributionModelingPresetListDocument,
  FinanceDistributionModelingPresetUpdateDocument,
  FinanceDistributionModelingRoundCreateDocument,
  FinanceDistributionModelingRoundDeleteDocument,
  FinanceDistributionModelingRoundListDocument,
  FinanceDistributionModelingRoundUpdateDocument,
  FinanceProductFundRoundInfoListDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";

import {
  ICreateIDistributionModelingPreset,
  ICreateIDistributionModelingRound,
  IDistributionModeling,
  IDistributionModelingInvestor,
  IDistributionModelingPreset,
  IDistributionModelingRound,
  IFinanceProductFundRoundInfoListArgs,
  IGetPerformanceDistributionModeling,
  IProductFundRoundDistributionModeling,
  IUpdateIDistributionModelingPreset,
  IUpdateIDistributionModelingRound,
} from "./types";

export async function getFinanceDistributionModelingPresetList(
  productFundId: number
): Promise<IDistributionModelingPreset[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: FinanceDistributionModelingPresetListDocument,
      variables: { productFundId },
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingPresetList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getFinanceDistributionModelingRoundList(
  productFundId: number
): Promise<IDistributionModelingRound[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: FinanceDistributionModelingRoundListDocument,
      variables: { productFundId },
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingRoundList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getProductFundRoundDistributionModelingList(
  args: IFinanceProductFundRoundInfoListArgs
): Promise<IProductFundRoundDistributionModeling[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: FinanceProductFundRoundInfoListDocument,
      variables: { productFundId: args.productFundId, sort: args.sort },
      fetchPolicy: "network-only",
    });

    return res?.data?.financeProductFundRoundInfoList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getFinanceDistributionModeling(variables: {
  productFundId: number;
  rounds: number[];
  presets: number[];
}): Promise<IDistributionModeling[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: FinanceDistributionModelingGetDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingGet ?? [];
  } catch (error) {
    throw error;
  }
}

export async function createFinanceDistributionModelingRound(
  variables: ICreateIDistributionModelingRound
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: FinanceDistributionModelingRoundCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingRoundCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateFinanceDistributionModelingRound(
  variables: IUpdateIDistributionModelingRound
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: FinanceDistributionModelingRoundUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingRoundUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteFinanceDistributionModelingRound(
  id: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: FinanceDistributionModelingRoundDeleteDocument,
      variables: { id },
      fetchPolicy: "no-cache",
    });

    return res?.data?.financeDistributionModelingRoundDelete ?? false;
  } catch (error) {
    throw error;
  }
}

export async function createFinanceDistributionModelingPreset(
  variables: ICreateIDistributionModelingPreset
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: FinanceDistributionModelingPresetCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingPresetCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateFinanceDistributionModelingPreset(
  variables: IUpdateIDistributionModelingPreset
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: FinanceDistributionModelingPresetUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingPresetUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteFinanceDistributionModelingPreset(
  id: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: FinanceDistributionModelingPresetDeleteDocument,
      variables: { id },
      fetchPolicy: "no-cache",
    });

    return res?.data?.financeDistributionModelingPresetDelete ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getFinanceDistributionModelingInvestorsList(
  productFundId: number
): Promise<IDistributionModelingInvestor[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: FinanceDistributionModelingInvestorsListDocument,
      variables: { productFundId },
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingInvestorsList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getFinanceDistributionModelingPerformance(variables: {
  productFundId: number;
  rounds: number[];
  presets: number[];
  investorId?: string | null;
}): Promise<IGetPerformanceDistributionModeling> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: FinanceDistributionModelingPerformanceDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.financeDistributionModelingPerformance ?? { vesting: [] };
  } catch (error) {
    throw error;
  }
}
